import { createSelector, createSlice } from "@reduxjs/toolkit";
import {
  getClientData,
  getClientDocuments,
  getClientSummary,
  getInbox,
} from "../shared/_services/inbox.services";
import {
  RECEIVED,
  applyMessageReceived,
  applyMessageSendFailed,
  applyMessageSendRetried,
  applyMessagesLoaded,
} from "./threadMessages";

// Pooled inbox SP2 — the advisor's inbox: the list (one filter at a time, as
// the server orders it), the server's unfiltered counts (the sidebar badge is
// counts.unread, R66), the one open thread, and a per-client cache of the
// three sidebar tabs (each tab is read once per open of the client's thread —
// opening it drops the cache, R89 — and every read writes a ReadLog row
// server-side, so the cache within an open is deliberate).
export const FILTERS = ["waiting", "mine", "all"];
export const TAB_IDS = ["summary", "data", "documents"];

// `row` is the open thread's last known list row and `rowIndex` where it
// last sat in the chip's rows: the list keeps the selected conversation in
// view when a refetch for the chip no longer returns it (R77).
const emptyOpen = () => ({
  threadId: null,
  thread: null,
  row: null,
  rowIndex: 0,
  advisors: [],
  messages: [],
  nextBefore: null,
  status: "idle",
  sendErrors: {},
});

const emptyTabs = () => ({
  summary: null,
  data: null,
  documents: null,
  status: { summary: "idle", data: "idle", documents: "idle" },
  errors: { summary: null, data: null, documents: null },
});

// The advisor opens on "Waiting on us", as the comp does (R70). `counts` is
// null until the first list or counts response arrives (R66).
const initialState = {
  filter: "waiting",
  rows: [],
  noMessagesYet: [],
  more: false,
  counts: null,
  total: 0,
  status: "idle",
  error: null,
  open: emptyOpen(),
  tabs: {},
};

// SP1's one definition of an advisor reply.
const isAdvisorReply = (m) =>
  m.senderType === "advisor" && (m.kind === "text" || m.kind === "file");
const isNewer = (current, candidate) =>
  !current || new Date(candidate) > new Date(current);
// The Summary's `lastReply` shape, from a saved message DTO; null for
// anything that is not an advisor reply (R79).
export const lastReplyOf = (m) =>
  m && !m.pending && isAdvisorReply(m)
    ? {
        by: {
          _id: m.sender?._id ?? null,
          firstName: m.sender?.firstName ?? "",
        },
        at: m.createdAt,
        text: m.content?.text ?? "",
      }
    : null;
const findRow = (state, threadId) =>
  threadId
    ? ([...state.rows, ...state.noMessagesYet].find(
        (r) => r.threadId === threadId,
      ) ?? null)
    : null;

const inboxSlice = createSlice({
  name: "inbox",
  initialState,
  reducers: {
    inboxLoading(state) {
      state.status = "loading";
      state.error = null;
    },
    inboxLoaded(state, { payload }) {
      // A response for a chip the advisor has since left must not overwrite
      // the list they are now looking at.
      if (payload.filter !== state.filter) return;
      state.rows = payload.rows || [];
      state.noMessagesYet = payload.noMessagesYet || [];
      state.more = payload.more === true;
      state.counts = payload.counts ?? state.counts;
      state.total = payload.total ?? state.rows.length;
      state.status = "ready";
      state.error = null;
      // The list is the one staff read that writes no ReadLog, so it is how
      // the open thread's owner line learns about a first-reply claim or a
      // take-over made elsewhere — never a re-GET of the thread. When the
      // chip no longer lists the open thread, refreshOpenRow reads it from
      // All instead (R77).
      const openId = state.open.threadId;
      const openRow = findRow(state, openId);
      if (openRow) {
        state.open.row = openRow;
        const at = state.rows.findIndex((r) => r.threadId === openId);
        if (at >= 0) state.open.rowIndex = at;
      }
      if (openRow && state.open.thread) {
        state.open.thread.owner = openRow.owner;
        state.open.thread.ownerIsMe = openRow.ownerIsMe;
      }
    },
    // The sidebar's one-row load (R66): counts only; the list is left alone.
    countsLoaded(state, { payload }) {
      if (payload) state.counts = payload;
    },
    inboxFailed(state, { payload }) {
      state.status = "error";
      state.error = payload ?? null;
    },
    filterChanged(state, { payload }) {
      if (FILTERS.includes(payload)) state.filter = payload;
    },
    rowMarkedRead(state, { payload: threadId }) {
      [...state.rows, ...state.noMessagesYet, state.open.row].forEach((r) => {
        if (r?.threadId === threadId) r.unread = false;
      });
    },
    openThreadLoading(state, { payload: threadId }) {
      // A reload of the same thread (Try again) keeps the row it knew.
      const same = state.open.threadId === threadId;
      const at = state.rows.findIndex((r) => r.threadId === threadId);
      state.open = {
        ...emptyOpen(),
        threadId,
        status: "loading",
        row: findRow(state, threadId) ?? (same ? state.open.row : null),
        rowIndex: at >= 0 ? at : same ? state.open.rowIndex : 0,
      };
    },
    openThreadLoaded(state, { payload: { thread, advisors } }) {
      if (!thread || String(thread._id) !== state.open.threadId) return;
      state.open.thread = thread;
      state.open.advisors = advisors || [];
      state.open.status = "ready";
    },
    openThreadFailed(state) {
      state.open.status = "error";
    },
    openMessagesLoaded(state, { payload }) {
      if (payload.threadId !== state.open.threadId) return;
      applyMessagesLoaded(state.open, payload);
    },
    openMessageReceived(state, { payload }) {
      if (
        !state.open.threadId ||
        String(payload.threadId) !== state.open.threadId
      )
        return;
      const result = applyMessageReceived(state.open, payload);
      const t = state.open.thread;
      if (result === RECEIVED.DUPLICATE || payload.pending || !t) return;
      // Keeps the header's waiting/answered line true without a re-GET.
      if (
        isAdvisorReply(payload) &&
        isNewer(t.lastAdvisorMessageAt, payload.createdAt)
      )
        t.lastAdvisorMessageAt = payload.createdAt;
      if (
        payload.senderType === "client" &&
        isNewer(t.lastClientMessageAt, payload.createdAt)
      )
        t.lastClientMessageAt = payload.createdAt;
    },
    openMessageSendFailed(state, { payload }) {
      applyMessageSendFailed(state.open, payload);
    },
    openMessageSendRetried(state, { payload: tempId }) {
      applyMessageSendRetried(state.open, tempId);
    },
    openThreadCleared(state) {
      state.open = emptyOpen();
    },
    ownerChanged(state, { payload: { threadId, owner, ownerIsMe } }) {
      if (state.open.thread && state.open.threadId === threadId) {
        state.open.thread.owner = owner;
        state.open.thread.ownerIsMe = ownerIsMe;
      }
      [...state.rows, ...state.noMessagesYet, state.open.row].forEach((r) => {
        if (r?.threadId === threadId) {
          r.owner = owner;
          r.ownerIsMe = ownerIsMe;
        }
      });
    },
    // The open thread's row as All lists it (R77), for the pinned row.
    openRowRefreshed(state, { payload: row }) {
      if (row && row.threadId === state.open.threadId) state.open.row = row;
    },
    tabLoading(state, { payload: { clientId, tab } }) {
      if (!state.tabs[clientId]) state.tabs[clientId] = emptyTabs();
      state.tabs[clientId].status[tab] = "loading";
      state.tabs[clientId].errors[tab] = null;
    },
    tabLoaded(state, { payload: { clientId, tab, data } }) {
      if (!state.tabs[clientId]) state.tabs[clientId] = emptyTabs();
      state.tabs[clientId][tab] = data;
      state.tabs[clientId].status[tab] = "ready";
    },
    tabFailed(state, { payload: { clientId, tab, message = null } }) {
      if (!state.tabs[clientId]) state.tabs[clientId] = emptyTabs();
      state.tabs[clientId].status[tab] = "error";
      state.tabs[clientId].errors[tab] = message;
    },
    // R89: opening a client's thread reads its tabs fresh, so a meeting
    // booked or a note saved elsewhere since the last open is shown.
    clientTabsReset(state, { payload: clientId }) {
      if (state.tabs[clientId]) delete state.tabs[clientId];
    },
    noteSaved(state, { payload: { clientId, note } }) {
      const summary = state.tabs[clientId]?.summary;
      if (summary) summary.note = note;
    },
    // R79: a reply saved while the Summary is cached is its new "Last reply
    // from us" — no re-read (every tab read writes a ReadLog row). An older
    // reply arriving late does not replace a newer one.
    summaryLastReplyPatched(state, { payload: { clientId, lastReply } }) {
      const summary = state.tabs[clientId]?.summary;
      if (summary && lastReply && isNewer(summary.lastReply?.at, lastReply.at))
        summary.lastReply = lastReply;
    },
  },
});

export const {
  inboxLoading,
  inboxLoaded,
  countsLoaded,
  inboxFailed,
  filterChanged,
  rowMarkedRead,
  openThreadLoading,
  openThreadLoaded,
  openThreadFailed,
  openMessagesLoaded,
  openMessageReceived,
  openMessageSendFailed,
  openMessageSendRetried,
  openThreadCleared,
  ownerChanged,
  openRowRefreshed,
  tabLoading,
  tabLoaded,
  tabFailed,
  clientTabsReset,
  noteSaved,
  summaryLastReplyPatched,
} = inboxSlice.actions;

// Loads the list for whichever filter is current. `quiet` is the live
// refetch (W6): no loader flash, and a failed refetch leaves the list as it was.
export const loadInbox =
  ({ quiet = false } = {}) =>
  async (dispatch, getState) => {
    const { filter } = getState().inbox;
    if (!quiet) dispatch(inboxLoading());
    try {
      const res = await getInbox({ filter });
      dispatch(inboxLoaded({ ...res.data, filter }));
    } catch (err) {
      if (!quiet) dispatch(inboxFailed(err?.response?.data?.message ?? null));
    }
  };

// R77: after a `thread updated` for the open thread, the active chip's
// refetch may no longer list it (Waiting once it is answered, Mine once it is
// taken over), so its owner line would go stale. One quiet read of All —
// the list writes no ReadLog — refreshes that row and the header's owner.
// Nothing to do on All, whose own refetch already carries the row.
export const refreshOpenRow = () => async (dispatch, getState) => {
  const { filter, open } = getState().inbox;
  const { threadId } = open;
  if (!threadId || filter === "all") return;
  try {
    const res = await getInbox({ filter: "all" });
    const row = [
      ...(res.data.rows || []),
      ...(res.data.noMessagesYet || []),
    ].find((r) => r.threadId === threadId);
    if (!row) return;
    dispatch(openRowRefreshed(row));
    dispatch(
      ownerChanged({ threadId, owner: row.owner, ownerIsMe: row.ownerIsMe }),
    );
  } catch (_) {
    /* the next refetch tries again; the header keeps what it knew */
  }
};

// The sidebar badge's source when the list has not been loaded on this page
// (R66): one row, for its `counts`. Does nothing once counts are known, or on
// a store with no inbox slice. `force` reads them again anyway: after a
// thread is marked read, and on `thread updated` off /inbox (R78).
export const loadCounts =
  ({ force = false } = {}) =>
  async (dispatch, getState) => {
    const inbox = getState().inbox;
    if (!inbox || (inbox.counts && !force)) return;
    try {
      const res = await getInbox({ limit: 1 });
      dispatch(countsLoaded(res.data.counts ?? null));
    } catch (_) {
      /* the badge keeps what it had; nothing to show the advisor */
    }
  };

const TAB_LOADERS = {
  summary: getClientSummary,
  data: getClientData,
  documents: getClientDocuments,
};

// Reads one tab once per client until clientTabsReset (R89). The status
// check runs against the store at dispatch time, so two quick selects of the
// same tab make one request.
// `retry` (the Try again control, R71) reads a tab whose load failed.
export const loadTab =
  (clientId, tab, { retry = false } = {}) =>
  async (dispatch, getState) => {
    const current = getState().inbox.tabs[clientId]?.status?.[tab] ?? "idle";
    if (!(current === "idle" || (retry && current === "error"))) return null;
    dispatch(tabLoading({ clientId, tab }));
    try {
      const res = await TAB_LOADERS[tab](clientId);
      dispatch(tabLoaded({ clientId, tab, data: res.data }));
      return null;
    } catch (err) {
      const message = err?.response?.data?.message ?? null;
      dispatch(tabFailed({ clientId, tab, message }));
      return message;
    }
  };

export const selectInboxFilter = (s) => s.inbox.filter;
export const selectInboxRows = (s) => s.inbox.rows;
export const selectNoMessagesYet = (s) => s.inbox.noMessagesYet;
export const selectInboxCounts = (s) => s.inbox.counts;
export const selectInboxStatus = (s) => s.inbox.status;
export const selectInboxError = (s) => s.inbox.error;
// The badge (R66): the server's unfiltered unread count for the caller.
// Guarded: DashboardSidebar renders in stores built without an `inbox` slice
// (SP1's badge tests build { thread } only) and must not throw there.
export const selectUnreadInboxCount = (s) => s.inbox?.counts?.unread ?? 0;
export const selectHasInboxCounts = (s) => s.inbox?.counts != null;
export const selectOpen = (s) => s.inbox.open;
export const selectOpenMessagesOldestFirst = createSelector(
  [(s) => s.inbox.open.messages],
  (messages) => [...messages].reverse(),
);
// The open thread's row: from the chip's rows, else the last one known for
// it (the chip may have stopped listing it, R77).
export const selectOpenRow = createSelector(
  [
    selectInboxRows,
    selectNoMessagesYet,
    (s) => s.inbox.open.threadId,
    (s) => s.inbox.open.row,
  ],
  (rows, none, threadId, known) =>
    threadId
      ? ([...rows, ...none].find((r) => r.threadId === threadId) ??
        (known?.threadId === threadId ? known : null))
      : null,
);
// The selected conversation when the chip's rows lack it, and where to show
// it (R77; the comp keeps the selected conversation whatever the filter).
export const selectPinnedRow = createSelector(
  [
    selectInboxRows,
    selectNoMessagesYet,
    (s) => s.inbox.open.row,
    (s) => s.inbox.open.rowIndex,
    (_, selectedThreadId) => selectedThreadId,
  ],
  (rows, none, row, rowIndex, selectedThreadId) =>
    selectedThreadId &&
    row?.threadId === selectedThreadId &&
    ![...rows, ...none].some((r) => r.threadId === selectedThreadId)
      ? { row, index: Math.min(rowIndex, rows.length) }
      : null,
);
export const selectOpenClient = createSelector(
  [
    selectOpenRow,
    (s) => s.inbox.open.thread?.clientId ?? null,
    (s) => s.inbox.tabs,
  ],
  (row, clientId, tabs) =>
    row?.client ??
    (clientId ? (tabs[clientId]?.summary?.client ?? null) : null),
);
export const selectClientTabs = (clientId) => (s) =>
  s.inbox.tabs[clientId] ?? null;

export default inboxSlice.reducer;
